<template>
	<BaseLayout :footer-full="true" :include-padding="false" :background-color-class="'bg-color-white'">
		<CorePlanHero :plan-hero="planHeroData">
			<template #heroCta>
				<div
					class="plan-cta-container pl-150-md ml-150-md mt-100-smo bl-md text-align-center-smo border-color-reversed"
				>
					<BaseLink class="button button-primary delta-md" :link="investNowLink" />
				</div>
			</template>
		</CorePlanHero>
		<section v-if="plan.strategySection" class="marketing-section">
			<div class="container">
				<header class="grid-md text-align-center mb-150">
					<h2 class="col-12 overline mb-100">Strategy</h2>
					<h3 class="heading-2 col-8-lg col-offset-2-lg">{{ plan.strategyHeadline }}</h3>
				</header>

				<div v-if="planStrategyCardControls.length" class="mt-200 text-align-center">
					<SegmentedControl
						:controls="planStrategyCardControls"
						:selected="selectedStrategy.key"
						@selected-segmented-control-key="changeStrategy"
					/>
				</div>

				<InfoCards
					:cards="planStrategyCards"
					:swap-order="true"
					:even-columns="true"
					:card-heading-style="'heading-2 font-weight-lighter line-height-125'"
					:class="{ 'fade-in-active': strategyCardToggled }"
					class="mt-300"
				/>
			</div>
		</section>

		<FeaturedAssetPanel
			v-if="plan.planFeaturedAsset"
			:asset-info="plan.planFeaturedAsset"
			:dark-text="plan.planFeaturedAsset.darkTheme"
		/>

		<article id="top-questions" class="marketing-section bg-color-greige">
			<div class="container">
				<div class="grid-md">
					<div class="col-10-md col-offset-1-md col-8-lg col-offset-2-lg">
						<h2 class="heading-2 text-align-center">Top Questions</h2>
						<QuestionList :question-items="topQuestions" class="mt-150" />
					</div>
				</div>
			</div>
		</article>

		<CTABanner>
			<template #link>
				<BaseLink class="button button-primary" data-test="select-plan-cta" :link="investNowLink" />
			</template>
		</CTABanner>
	</BaseLayout>
</template>

<script setup lang="ts">
import { appBasePath, appCloudfrontPath } from '@constants';
import { computed, ref } from 'vue';
import { InfoCard, Link, QuestionItem, SegmentedControlButton } from 'types/layout';
import { PlanHero, ReturnsTarget } from 'types/plan';
import CorePlanHero from '@components/plan/core-plan-hero.vue';
import CTABanner from '@components/layout/cta-banner.vue';
import FeaturedAssetPanel from '@components/marketing-site/featured-asset-panel.vue';
import InfoCards from '@components/layout/info-cards.vue';
import { plansInfo } from '@constants/plans-info';
import QuestionList from '@components/layout/question-list.vue';
import SegmentedControl from '@components/segmented-control/segmented-control.vue';
import { useHead } from '@unhead/vue';

interface Props {
	planSlug: string;
}
const props = defineProps<Props>();

const selectedStrategyKey = ref('');
const strategyCardToggled = ref(true);
const plan = plansInfo.filter((planInfo) => planInfo.urlSlug === props.planSlug)[0];
const topQuestions: Array<QuestionItem> = [
	{
		question: 'What am I investing in?',
		answer: 'You are investing in a diversified portfolio of real estate projects located throughout the United States that our team identifies, acquires, and manages on your behalf. The approximate current allocation is displayed in the <a href="#portfolio" class="text-link">Portfolio</a> section above. However, you should expect it to grow and change over time as we acquire more projects, and others are sold or pay off.'
	},
	{
		question: 'How do returns work at Fundrise?',
		answer: "Real estate famously offers investors a unique mix of long-term appreciation and cash flow potential. Investing with Fundrise is no different. Fundrise investor portfolios have the potential to generate dividends on a quarterly basis while their shares also grow in value over time. These returns ultimately come from the individual real estate assets in your portfolio – interest or rental income collected, as well as potential appreciation in the property's value."
	},
	{
		question: 'Is Fundrise a long-term investment?',
		answer: "Absolutely. Like all private real estate investments, investments in Fundrise should be viewed as long-term (5+ years). This is because nearly all of the most effective real estate investing strategies require a combination of both expertise and time to pay off. We select strategies based on their long-term return potential for our investors, not short-term optics. If you anticipate needing your investment back in the near-term, we don't recommend investing with us."
	},
	{
		question: 'How does liquidity work at Fundrise?',
		answer: `While Fundrise should be viewed as a long-term investment, we understand that investors may want or need to prematurely liquidate (or "redeem") their shares. Accordingly, our investors may request to redeem shares at any time, although such redemption cannot be guaranteed — especially in times of economic uncertainty — and there may be costs associated with premature redemption. For a full description of our redemption policy please see the disclosure in the offering circulars on <a class="text-link" href="${appBasePath}/oc?cta=Top%20Questions">fundrise.com/oc</a>.`
	},
	{
		question: 'How does pricing work?',
		answer: "Investors pay a 0.15% annual investment advisory fee, which may be waived under certain circumstances. This means that over a 12-month period, you will pay a $1.50 advisory fee for every $1,000 you've invested with us. Separately, the funds in our standard portfolios pay a 0.85% annual asset management fee. That's $8.50/year for every $1,000 invested. You can find a full description of all fees in our offering circulars."
	},
	{
		question: 'What are eREITs and eFunds?',
		answer: 'eREITs and eFunds are the limited liability companies that hold the real estate projects in your Fundrise portfolio. When you invest, you are actually buying shares of these companies, allowing you to diversify into many properties efficiently and at a low cost. The current weighting of eREITs and eFunds in this plan is listed below.'
	},
	{
		question: 'What tax documents can I expect to receive?',
		answer: 'Investors in the Supplemental Income plan can expect to receive a <a href="https://www.irs.gov/pub/irs-pdf/i1099div.pdf" class="text-link" target="_blank">Form 1099-DIV</a> for each eREIT in the plan’s allocation.'
	}
];

const investNowLink: Link = {
	text: 'Invest now',
	router: 'get-started'
};

const planStrategyCardControls = computed((): Array<SegmentedControlButton> => {
	if (plan.strategySection && plan.strategySection.length > 1) {
		const sections = [];

		for (const section of plan.strategySection) {
			if (section.strategyType) {
				sections.push({
					key: section.strategyType.toLowerCase(),
					label: section.strategyType
				});
			}
		}

		return sections;
	}

	return [];
});

const selectedStrategy = computed((): SegmentedControlButton => {
	if (planStrategyCardControls.value.length) {
		return (
			planStrategyCardControls.value.find((control) => control.key === selectedStrategyKey.value) ??
			planStrategyCardControls.value[0]
		);
	} else {
		return { key: '', label: '' };
	}
});

const planMetaImage = computed((): string => {
	switch (props.planSlug) {
		case 'supplemental-income':
			return `${appCloudfrontPath}/images/marketing-site/ria/Supplemental-Income-Hero.png`;
		case 'balanced-investing':
			return `${appCloudfrontPath}/images/marketing-site/ria/Balanced-Investing-Hero.png`;
		case 'long-term-growth':
			return `${appCloudfrontPath}/images/marketing-site/ria/Long-Term-Growth-Hero.png`;
		default:
			return '';
	}
});

const planReturnsProfile = computed((): Array<ReturnsTarget> => {
	return [
		{
			name: 'Dividends',
			return: plan.targetReturnProfile?.dividends || 0
		},
		{
			name: 'Appreciation',
			return: plan.targetReturnProfile?.appreciation || 0
		},
		{
			name: 'Total return',
			return: plan.targetReturnProfile?.totalReturn || 0
		}
	];
});

const planHeroData = computed((): PlanHero => {
	return {
		slug: plan.urlSlug,
		heroGraphic: plan.planGraphic ? appCloudfrontPath + plan.planGraphic : '',
		name: plan.name,
		headline: plan.headline || '',
		returns: planReturnsProfile.value
	};
});

const planStrategyCards = computed((): Array<InfoCard> => {
	return (
		plan.strategySection?.find((section) => section.strategyType?.toLowerCase() === selectedStrategy.value.key)
			?.strategyDetail ?? []
	);
});

function changeStrategy(strategyKey: string): void {
	if (selectedStrategyKey.value !== strategyKey) {
		selectedStrategyKey.value = strategyKey;
		strategyCardToggled.value = true;
		setTimeout(() => {
			strategyCardToggled.value = false;
		}, 1500);
	}
}

function setup(): void {
	useHead({
		title: `${plan.name} | Investment Plans`,
		meta: [
			{ property: 'og:title', content: `${plan.name} | Investment Plans` },
			{ name: 'description', content: plan.headline },
			{ property: 'og:description', content: plan.headline },
			{ property: 'og:image', content: planMetaImage }
		]
	});
}

setup();
</script>
